import { useState, useEffect, useCallback, useRef } from 'react';

const QUEUE_KEY = 'bilanexpress_offline_queue';
const DASHBOARD_CACHE_KEY = 'bilanexpress_dashboard_cache';

interface QueuedRequest {
  id: string;
  url: string;
  method: string;
  body: string;
  timestamp: number;
}

interface DashboardCache {
  data: any;
  timestamp: number;
}

export function useOfflineQueue() {
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );
  const [queueLength, setQueueLength] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const syncingRef = useRef(false);

  // Update queue length from localStorage
  const refreshQueueLength = useCallback(() => {
    try {
      const queue = JSON.parse(localStorage.getItem(QUEUE_KEY) || '[]');
      setQueueLength(queue.length);
    } catch {
      setQueueLength(0);
    }
  }, []);

  // Listen for online/offline events
  useEffect(() => {
    const goOnline = () => {
      setIsOnline(true);
      replayQueue();
    };
    const goOffline = () => setIsOnline(false);

    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    refreshQueueLength();

    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  // Queue a failed request
  const enqueue = useCallback((url: string, method: string, body: any) => {
    try {
      const queue: QueuedRequest[] = JSON.parse(localStorage.getItem(QUEUE_KEY) || '[]');
      queue.push({
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        url,
        method,
        body: JSON.stringify(body),
        timestamp: Date.now(),
      });
      localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
      refreshQueueLength();
    } catch {
      // localStorage full or unavailable
    }
  }, [refreshQueueLength]);

  // Replay queued requests on reconnect
  const replayQueue = useCallback(async () => {
    if (syncingRef.current) return;
    syncingRef.current = true;
    setSyncing(true);

    try {
      const queue: QueuedRequest[] = JSON.parse(localStorage.getItem(QUEUE_KEY) || '[]');
      const remaining: QueuedRequest[] = [];

      for (const req of queue) {
        try {
          const res = await fetch(req.url, {
            method: req.method,
            headers: { 'Content-Type': 'application/json' },
            body: req.body,
          });
          if (!res.ok) {
            remaining.push(req);
          }
        } catch {
          remaining.push(req);
        }
      }

      localStorage.setItem(QUEUE_KEY, JSON.stringify(remaining));
      refreshQueueLength();
    } finally {
      syncingRef.current = false;
      setSyncing(false);
    }
  }, [refreshQueueLength]);

  // Wrapper for fetch that queues on failure when offline
  const offlineFetch = useCallback(async (url: string, options: RequestInit = {}) => {
    if (navigator.onLine) {
      try {
        const res = await fetch(url, options);
        return res;
      } catch {
        // Network error — queue if POST
        if (options.method === 'POST') {
          enqueue(url, options.method, options.body ? JSON.parse(options.body as string) : {});
        }
        throw new Error('Hors ligne — requête mise en file d\'attente');
      }
    } else {
      if (options.method === 'POST') {
        enqueue(url, options.method, options.body ? JSON.parse(options.body as string) : {});
        throw new Error('Hors ligne — requête mise en file d\'attente');
      }
      throw new Error('Hors ligne');
    }
  }, [enqueue]);

  return {
    isOnline,
    queueLength,
    syncing,
    offlineFetch,
    replayQueue,
  };
}

// Dashboard cache utilities
export function cacheDashboardData(data: any) {
  try {
    const cache: DashboardCache = { data, timestamp: Date.now() };
    localStorage.setItem(DASHBOARD_CACHE_KEY, JSON.stringify(cache));
  } catch {
    // localStorage full
  }
}

export function getCachedDashboard(): { data: any; isStale: boolean } | null {
  try {
    const raw = localStorage.getItem(DASHBOARD_CACHE_KEY);
    if (!raw) return null;
    const cache: DashboardCache = JSON.parse(raw);
    const oneHour = 60 * 60 * 1000;
    const isStale = Date.now() - cache.timestamp > oneHour;
    return { data: cache.data, isStale };
  } catch {
    return null;
  }
}
